import { ILineChartProps, LineChart } from "@fluentui/react-charting";
import { format } from "d3-format";
import React from "react";
import { CustomShimmer } from "../helper/CustomShimmer";
import { IResizeLineChartProps } from "../interfaces/resize-linechart-props.interfaces";

export class LineChartComponent extends React.Component<IResizeLineChartProps, ILineChartProps> {
  private tickValues: Date[] = [];
  constructor(props: IResizeLineChartProps) {
    super(props);
    this.state = {
      width: props.width,
      height: props.height,
      allowMultipleShapesForPoints: !!props.allowMultipleShapesForPoints,
      data: props.data,
      xAxisTitle: props.xAxisTitle,
      yAxisTitle: props.yAxisTitle
    };
    setTimeout(() => {
      const grigrpElements = document.querySelectorAll("[role='listbox']");
      grigrpElements.forEach(function (element) {
        element.setAttribute("aria-busy", "true");
      });
    }, 1000);
  }

  componentDidUpdate(prevProps: ILineChartProps, prevState: ILineChartProps) {
    if (!this.props.allowResize) return;
    if (this.props.width !== prevProps.width || this.state.width !== prevState.width) {
      this._onWidthChange(this.props.width);
    }
    // Add this line to handle height changes
    if (this.props.height !== prevProps.height) {
      this.setState({ height: this.props.height });
    }
  }

  public render(): React.JSX.Element {
    this.tickValues = this._getTickValues();
    return <div>{this._basicExample()}</div>;
  }

  private _onWidthChange = (width?: number) => {
    this.setState({ width });
  };

  private _onHeightChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ height: parseInt(e.target.value, 10) });
  };

  private formatBillions = (fs: any) => (s: any) => {
    const value = Number(s);
    const formattedValue = format(fs)(value);
    if (this.props.selectedFamily === "Microsoft Sentinel") {
      return formattedValue.replace(/G$/, " GB");
    }
    return formattedValue.replace(/G$/, "B");
  };

  private formatSecurityCopilotNumbers = (s: any) => {
    const value = Number(s);
    return value.toFixed(1);
  };

  private _showNoGraphData(): React.JSX.Element {
    const rootStyle = {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      height: "inherit"
    };
    return <div style={rootStyle}>Graph has no data to display</div>;
  }

  private _getTickValues = (): Date[] => {
    const dts = new Set<any>([]);
    this.props.data.lineChartData?.map((m) => m.data.map((d) => dts.add((d.x as Date).toISOString())));
    return Array.from(dts).map((m) => new Date(m));
  };

  private _getTickFormat(): string {
    if (this.props.granularity === "Daily") {
      return "%m/%d"; // MM/DD/YY
    }
    return "%b %y"; // default: "May 25"
  }
  private _basicExample(): React.JSX.Element {
    const rootStyle = {
      width: `${this.state.width}px`,
      height: `${this.state.height}px`
    };

    return this.props?.isLoading ? (
      <CustomShimmer columnCount={4} rowCount={8} styles={{ root: { height: `${this.state.height}px` || "300px" } }} />
    ) : (
      <div style={rootStyle}>
        {(
          !this.props.data.lineChartData ||
          !Array.isArray(this.props.data.lineChartData) ||
          this.props.data.lineChartData.length === 0 ||
          this.props.data.lineChartData.every(
            (series) => !series.data || series.data.length === 0 || series.data.every((point) => !point.y)
          )
        ) ? (
          <div
            style={{
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 15
            }}
          >
            No data available to display, Try changing filters.
          </div>
        ) : (
          <LineChart
            legendsOverflowText={"Overflow Items"}
            height={this.state.height}
            width={this.state.width}
            tickFormat={this._getTickFormat()}
            tickValues={this.tickValues}
            allowMultipleShapesForPoints={this.state.allowMultipleShapesForPoints}
            enablePerfOptimization={true}
            strokeWidth={2}
            yAxisTitle={this.state.yAxisTitle}
            xAxisTitle={this.state.xAxisTitle}
            yAxisTickFormat={
              this.props.selectedFamily === "Security Copilot"
                ? this.formatSecurityCopilotNumbers
                : this.formatBillions("0.2s")
            }
            rotateXAxisLables={true}
            legendProps={{
              canSelectMultipleLegends: true,
              allowFocusOnLegends: true,
              overflowText: "more"
            }}
            useUTC={true}
            {...this.props}
          />
        )}
      </div>
    );
  }
}
